import { Icon } from 'ol/style';

export type RenderingContext = {
  size: [number, number];
  ctx: CanvasRenderingContext2D;
};

export function renderMarker(
  contentRenderer: (context: RenderingContext) => void
) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  const RADIUS = 20;
  const BORDER_THICKNESS = 3;
  const ARROW_WIDTH = 5;
  const ARROW_HEIGHT = 8;
  const SHADOW_BLUR_RADIUS = 10;

  const diameter = RADIUS * 2;
  const iconSize = [
    diameter + SHADOW_BLUR_RADIUS * 2,
    diameter + ARROW_HEIGHT + SHADOW_BLUR_RADIUS * 2,
  ];
  const circleCenterOffset = RADIUS + SHADOW_BLUR_RADIUS;
  const arrowOffset = [
    circleCenterOffset,
    circleCenterOffset + RADIUS - BORDER_THICKNESS / 2,
  ];
  const innerCircleRadius = RADIUS - BORDER_THICKNESS;
  const innerCircleDiameter = innerCircleRadius * 2;
  const contentOffset = BORDER_THICKNESS + SHADOW_BLUR_RADIUS;
  const innerCircleOffset = contentOffset + innerCircleRadius;

  ctx.beginPath();
  ctx.moveTo(arrowOffset[0] - ARROW_WIDTH, arrowOffset[1]);
  ctx.lineTo(arrowOffset[0] + ARROW_WIDTH, arrowOffset[1]);
  ctx.lineTo(arrowOffset[0], arrowOffset[1] + ARROW_HEIGHT);
  ctx.lineTo(arrowOffset[0] - ARROW_WIDTH, arrowOffset[1]);
  ctx.closePath();

  ctx.fillStyle = '#fff';
  ctx.shadowBlur = SHADOW_BLUR_RADIUS;
  ctx.shadowColor = 'rgba(0, 0, 0, 25%)';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(circleCenterOffset, circleCenterOffset, RADIUS, 0, Math.PI * 2, true);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.arc(
    innerCircleOffset,
    innerCircleOffset,
    innerCircleRadius,
    0,
    Math.PI * 2,
    true
  );
  ctx.closePath();
  ctx.clip();

  ctx.translate(contentOffset, contentOffset);
  contentRenderer({ ctx, size: [innerCircleDiameter, innerCircleDiameter] });

  return new Icon({
    crossOrigin: 'anonymous',
    anchor: [0.5, 1],
    anchorXUnits: 'fraction',
    anchorYUnits: 'fraction',
    src: undefined,
    img: canvas,
    imgSize: canvas ? iconSize : undefined,
  });
}

export function renderMarkerWithGradient(
  contentRenderer: (context: RenderingContext) => void
) {
  const gradientStops = ['#a493d8', '#5f72e9'];
  return renderMarker((context) => {
    const { ctx, size } = context;
    const gradient = ctx.createLinearGradient(0, 0, size[0], size[1]);
    for (const [i, color] of gradientStops.entries())
      gradient.addColorStop(i, color);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size[0], size[1]);
    contentRenderer(context);
  });
}

export function renderMarkerWithText(
  text: string,
  options: {
    contentRenderer?: (context: RenderingContext) => void;
  } = {}
) {
  return renderMarkerWithGradient((context) => {
    const { ctx, size } = context;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const fontSize = size[1] / 2;
    ctx.font = `${fontSize}px sans-serif`;
    ctx.fillStyle = '#fff';
    ctx.fillText(text, size[0] / 2, size[1] / 2, size[0]);

    options.contentRenderer?.(context);
  });
}

export type RenderMarkerWithImageOptions = {
  sizeFr?: number;
};

export function renderMarkerWithImage(
  src: string,
  alt: string,
  options: RenderMarkerWithImageOptions = {}
) {
  const { sizeFr = 1 } = options;
  return renderMarkerWithText(alt, {
    contentRenderer: (context) => {
      const { ctx, size } = context;
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        const sizeScaled = [size[0] * sizeFr, size[1] * sizeFr];
        const halfSize = [size[0] / 2, size[1] / 2];
        const halfSizeScaled = [halfSize[0] * sizeFr, halfSize[1] * sizeFr];
        ctx.drawImage(
          image,
          halfSize[0] - halfSizeScaled[0],
          halfSize[1] - halfSizeScaled[1],
          sizeScaled[0],
          sizeScaled[1]
        );
      };
      image.src = src;
    },
  });
}
